import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/redux/store';
import { questionnairesApi } from 'app/services/questionnaires';
import { resetQuestionnaire } from '../../actions';
import lodashSet from 'lodash.set';

export interface RentalActivityStateValues {
  doesTheLlcOwnTheRentalProperty: Nullable<TYesOrNo>;
  rentalPropertyList: rentalPropertyList;
}

export interface RentalActivityState {
  values: RentalActivityStateValues;
}
type OtherExpensesList = OtherExpensesListRow[];
export type OtherExpensesListRow = {
  name: Nullable<string>;
  amount: Nullable<string>;
};
type rentalPropertyList = rentalPropertyListRow[];
export type rentalPropertyListRow = {
  whatTypeOfPropertyIsThisRentalProperty: Nullable<string>;
  otherDescription: Nullable<string>;
  streetAddress: Nullable<string>;
  city: Nullable<string>;
  state: Nullable<string>;
  zipCode: Nullable<string>;
  country: Nullable<string>;
  howManyForms1099AreYouRequiredToFileForPaymentsYouMadeThisYear: Nullable<TYesOrNo>;
  pleaseUploadACopyOfTheHudClosingStatement: Nullable<string>;
  datePropertyWasPurchased: Nullable<string>;
  purchasePrice: Nullable<string>;
  daysRentedInTaxYear: Nullable<string>;
  datePropertyBeganBeingRented: Nullable<string>;
  doYouHaveAPriorDepreciationScheduleifFiledPreviousTaxReturn: Nullable<TYesOrNo>;
  pleaseUploadACopyOfPriorDepreciationSchedule: Nullable<string>;
  ifNoRentalIncomeWasEarnedInTaxYearWasPropertyAvailableToBeRented: Nullable<TYesOrNo>;
  datePropertyWasAvailableToBeRentedOut: Nullable<string>;
  wasThisPropertySoldThisYear: Nullable<TYesOrNo>;
  didYouHaveAProfessionalAtAccountantsOnAirPrepareFinancialStatementsForThisPropertyThisYear: Nullable<TYesOrNo>;
  congratulationsThatMeansMuchLessWorkForYouOnThisTaxInformationweWillImportYourFinancialStatementsDirectlyFromOurBookkeepingSoftware: Nullable<string>;
  pleaseIncludeYourYearRevenuesAndExpensesForThisRentalPropertyBelow: Nullable<string>;
  rentsReceived: Nullable<string>;
  cleaningAndMaintenance: Nullable<string>;
  commissions: Nullable<string>;
  insurance: Nullable<string>;
  legalAndOtherProfessionalFees: Nullable<string>;
  mangementFees: Nullable<string>;
  mortgageInterestPaidToBanksEtc: Nullable<string>;
  otherInterest: Nullable<string>;
  repairs: Nullable<string>;
  supplies: Nullable<string>;
  taxes: Nullable<string>;
  travel: Nullable<string>;
  utilities: Nullable<string>;
  otherExpenses: Nullable<string>;
  otherExpensesList: OtherExpensesList;
  totalExpenses: Nullable<string>;
  profitloss: Nullable<string>;
};
const initialState: RentalActivityState = {
  values: {
    doesTheLlcOwnTheRentalProperty: null,
    rentalPropertyList: [],
  },
};

const RentalActivitySlice = createSlice({
  name: 'RentalActivity',
  initialState,
  reducers: {
    reset(state) {
      state = initialState;
    },
    update(state, action: PayloadAction<RentalActivityStateValues>) {
      state.values = action.payload;
    },
    updateKey(
      state,
      action: PayloadAction<{
        key: keyof RentalActivityStateValues;
        value: any;
      }>,
    ) {
      lodashSet(state, `values.${action.payload.key}`, action.payload.value);
    },

    // Update KEY_HERE_LIST if there is a Record Here
    updateFieldArrayKey(
      state: any,
      action: PayloadAction<{
        key: keyof RentalActivityStateValues | 'otherExpensesList';
        value: any;
      }>,
    ) {
      const fieldArray =
        action.payload.value &&
        action.payload.value.map(({ ...field }) => ({
          ...field,
        }));
      if (
        action.payload.key === 'otherExpensesList' ||
        action.payload.key === 'rentalPropertyList'
      ) {
        state.values[action.payload.key] = fieldArray;
      } else {
        lodashSet(state, `values.${action.payload.key}`, fieldArray);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetQuestionnaire, () => initialState);

    builder.addMatcher(
      questionnairesApi.endpoints.getQuestionnaireById.matchFulfilled,
      (state, { payload }) => {
        state.values = payload.questionnaire.data.taxQuestionnaire
          ? payload.questionnaire.data.taxQuestionnaire?.additionalQuestions
              ?.rentalActivity?.values
          : initialState.values;
      },
    );
  },
});

export const { update, updateKey, updateFieldArrayKey } =
  RentalActivitySlice.actions;
export default RentalActivitySlice.reducer;

export const selectRentalActivity = (state: RootState) =>
  state.taxQuestionnaire.additionalQuestions.rentalActivity;
